import { useCallback, useEffect, useMemo } from "react";
import { Box } from "@material-ui/core";
import { Message } from "@sendbird/uikit-react/GroupChannel/components/Message";
import MessageList from "@sendbird/uikit-react/GroupChannel/components/MessageList";
import { useGroupChannelContext } from "@sendbird/uikit-react/GroupChannel/context";
import type { GroupChannelMessageListProps } from "@sendbird/uikit-react/GroupChannel/components/MessageList";
import type { RenderMessageParamsType } from "@sendbird/uikit-react/types/types";
import {
  ensureSendbirdMessagesDir,
  getFirstVisibleMessageId,
  getMinScrollTop,
  getOldestMessageCreatedAt,
  isChatMessageWithinRetentionPeriod,
} from "../../utils/chat/messageRetention";
import { MessageHistoryButton } from "./MessageHistoryButton";
import { useGroupChatHasHistory } from "../../hooks/useGroupChatHasHistory";

type ChatMessageRetentionScrollGuardProps = {
  cutoff: number;
};

type ChatMessageRetentionMessageListProps = GroupChannelMessageListProps & {
  channelUrl: string | null;
  cutoff: number;
};

/** 期限より前のメッセージ取得を抑止する */
export const ChatMessageRetentionScrollGuard = ({
  cutoff,
}: ChatMessageRetentionScrollGuardProps) => {
  const { messages, scrollRef } = useGroupChannelContext();

  const isRetentionReached = useMemo(() => {
    const oldestCreatedAt = getOldestMessageCreatedAt(messages);
    return oldestCreatedAt !== null && oldestCreatedAt <= cutoff;
  }, [messages, cutoff]);

  useEffect(() => {
    const scrollElement = scrollRef?.current;
    if (!scrollElement || !isRetentionReached) {
      return undefined;
    }

    const clampScrollTop = () => {
      const minTop = getMinScrollTop(scrollElement);
      if (scrollElement.scrollTop < minTop) {
        scrollElement.scrollTop = minTop;
      }
    };

    clampScrollTop();
    scrollElement.addEventListener("scroll", clampScrollTop, { passive: true });
    return () => scrollElement.removeEventListener("scroll", clampScrollTop);
  }, [scrollRef, isRetentionReached, messages.length]);

  return null;
};

/**
 * メッセージ一覧先頭に「過去の履歴はこちら」を出す。
 * 表示可否は has_history API で判定する。
 */
export const ChatMessageRetentionMessageList = ({
  channelUrl,
  cutoff,
  renderMessage,
  ...restProps
}: ChatMessageRetentionMessageListProps) => {
  const { messages, loading, currentChannel, hasPrevious } =
    useGroupChannelContext();

  const messagesFingerprint = useMemo(
    () => messages.map((message) => message.messageId).join(","),
    [messages],
  );
  const oldestCreatedAt = useMemo(
    () => getOldestMessageCreatedAt(messages),
    [messages],
  );
  const buttonAnchorMessageId = useMemo(
    () => getFirstVisibleMessageId(messages, cutoff),
    [messages, cutoff],
  );
  const hasOnlyOldMessages = useMemo(
    () =>
      messages.length > 0 &&
      messages.every(
        (message) =>
          !isChatMessageWithinRetentionPeriod(message.createdAt, cutoff),
      ),
    [messages, cutoff],
  );
  const reachedRetentionEdge =
    oldestCreatedAt !== null && oldestCreatedAt <= cutoff;

  const isChannelSynced =
    Boolean(channelUrl) && currentChannel?.url === channelUrl;
  const hasNoSendbirdMessages =
    !loading && isChannelSynced && messages.length === 0;
  // 未初期化時 hasPrevious() は false のため、messages があるときだけ末尾とみなす
  const isSendbirdHistoryExhausted =
    !loading &&
    isChannelSynced &&
    messages.length > 0 &&
    !hasPrevious();

  const { hasHistory, channelId: historyChannelId } = useGroupChatHasHistory({
    channelUrl,
    isChannelSynced,
    loading,
    cutoff,
    messagesFingerprint,
    anchorMessageId: buttonAnchorMessageId,
  });

  // dir が無いと created-at の left/right:-84px が効かず吹き出しに重なる
  useEffect(() => {
    if (loading) {
      return;
    }
    ensureSendbirdMessagesDir();
  }, [loading, channelUrl, messagesFingerprint]);

  const showButton =
    hasHistory &&
    (reachedRetentionEdge ||
      hasOnlyOldMessages ||
      hasNoSendbirdMessages ||
      isSendbirdHistoryExhausted);

  const renderMessageWithRetentionButton = useCallback(
    (messageProps: RenderMessageParamsType) => {
      // 保持期間外は描画しない（空枠＋日付のちらつき防止）
      if (
        !isChatMessageWithinRetentionPeriod(
          messageProps.message.createdAt,
          cutoff,
        )
      ) {
        return <></>;
      }

      return (
        <>
          {showButton &&
            messageProps.message.messageId === buttonAnchorMessageId && (
              <MessageHistoryButton
                channelId={historyChannelId}
                lastCreatedAt={cutoff}
                lastMessageId={buttonAnchorMessageId}
              />
            )}
          {renderMessage ? (
            renderMessage(messageProps)
          ) : (
            <Message {...messageProps} />
          )}
        </>
      );
    },
    [
      showButton,
      buttonAnchorMessageId,
      historyChannelId,
      renderMessage,
      cutoff,
    ],
  );

  if ((hasOnlyOldMessages || hasNoSendbirdMessages) && !loading) {
    return (
      <Box
        className="sendbird-conversation__message-list"
        style={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          height: "100%",
        }}
      >
        {hasHistory ? (
          <MessageHistoryButton
            channelId={historyChannelId}
            lastCreatedAt={cutoff}
            lastMessageId={buttonAnchorMessageId}
          />
        ) : null}
      </Box>
    );
  }

  return (
    <MessageList
      {...restProps}
      className="sendbird-conversation__message-list"
      renderMessage={renderMessageWithRetentionButton}
    />
  );
};
